import React, { useEffect, useState } from "react";
import { getRequest } from "../../service";
import { getEmployeeId } from "../../utils/localStorage";
import { formatDate, nowInUserTz } from "../../utils/dateTime";
import { Spin, Pagination, Tag } from "antd";

const RequestTable = () => {
  const heading = ["Employee Name", "Date", "Status"];
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [current, setCurrent] = useState(1);
  const pageSize = 3;

  useEffect(() => {

    const fetchData = async () => {
      setLoading(true);
      try {
        // Get current date and format it for API
        const today = new Date();
        const startDateStr = today.toISOString().split('T')[0]; // YYYY-MM-DD format
        const endDateStr = today.toISOString().split('T')[0]; // YYYY-MM-DD format
        
        const companyId = getEmployeeId();
        
        // Fetch all employees and attendance data in parallel
        const [employeesResponse, attendanceResponse] = await Promise.all([
          getRequest('/api/companies/reportto'),
          getRequest(`/api/myattendance/byCompany?companyId=${companyId}&startDate=${startDateStr}&endDate=${endDateStr}`)
        ]);

        // Get all employees
        const allEmployees = employeesResponse?.employees || [];
        
        // Get attendance logs for today
        const attendanceLogs = attendanceResponse?.attendanceLogs || [];
        
        // Create a set of employee IDs who have attendance today
        const presentEmployeeIds = new Set();
        attendanceLogs.forEach(log => {
          if (log.employeeId) {
            presentEmployeeIds.add(log.employeeId);
          }
        });

        // Find employees who are absent (not in attendance logs)
        const absentEmployees = allEmployees.filter(employee => 
          !presentEmployeeIds.has(employee.id || employee._id)
        );

        // Format the data for display
        const absentData = absentEmployees.map(employee => ({
          employeeName: employee.name || employee.firstName + ' ' + employee.lastName || 'Unknown',
          date: startDateStr,
          status: 'Absent',
          _id: employee.id || employee._id
        }));

        setData(absentData);
      } catch (err) {
        console.error('Error fetching data:', err);
        setData([]);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Helper to get Tag color and style based on status
  const getStatusTag = (status) => {
    const normalized = (status || "").toLowerCase();
    if (normalized === "absent") {
      return (
        <Tag
          color="#fff1f0"
          style={{
            color: "#ff4d4f",
            borderColor: "#ffa39e",
            background: "#fff1f0",
            fontWeight: 500,
            fontSize: 12,
            padding: "2px 10px",
            borderRadius: 6,
            borderWidth: 1,
            borderStyle: "solid",
            display: "inline-block",
            minWidth: 60,
            textAlign: "center",
          }}
        >
          Absent
        </Tag>
      );
    }
    // Default
    return (
      <Tag
        style={{
          color: "#595959",
          borderColor: "#d9d9d9",
          background: "#fafafa",
          fontWeight: 500,
          fontSize: 14,
          padding: "2px 12px",
          borderRadius: 6,
          borderWidth: 1,
          borderStyle: "solid",
          display: "inline-block",
          minWidth: 60,
          textAlign: "center",
        }}
      >
        {status}
      </Tag>
    );
  };

  // Pagination logic
  const startIdx = (current - 1) * pageSize;
  const endIdx = startIdx + pageSize;
  const pageData = data.slice(startIdx, endIdx);

  return (
    <Spin spinning={loading} size="large">
      <div className="w-full overflow-x-auto">
        {/* Total count display */}
        <div className="mb-4 p-3 bg-gray-50 rounded-lg">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600">
              <span className="font-semibold text-lg text-red-600">{data.length}</span> Employees absent today
            </div>
            <div className="text-xs text-gray-500">
              {nowInUserTz().format('dddd, MMMM D, YYYY')}
            </div>
          </div>
        </div>
        
        <table className="min-w-[600px] w-full bg-white ">
          <thead>
            <tr className="text-left border-b border-gray-200">
              {heading.map((item) => (
                <th
                  className="px-2 py-2 md:px-4 md:py-2 text-[13px] md:text-[14px] font-semibold text-primary whitespace-nowrap"
                  key={item}
                >
                  {item}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {pageData.length === 0 ? (
              <tr>
                <td colSpan={heading.length} className="px-2 py-4 md:px-4 text-center text-gray-500">
                  No absent employees found for today.
                </td>
              </tr>
            ) : (
              pageData.map((item) => (
                <tr className="border-b border-gray-200" key={item._id}>
                  <td className="px-2 py-2 md:px-4 md:py-1 text-[13px] md:text-[14px] text-gray-700 whitespace-nowrap">{item.employeeName}</td>
                  <td className="px-2 py-2 md:px-4 md:py-1 text-[13px] md:text-[14px] text-gray-700 whitespace-nowrap">{formatDate(item.date)}</td>
                  <td className="px-2 py-2 md:px-4 md:py-1 text-[13px] md:text-[14px] text-gray-700 whitespace-nowrap">
                    {getStatusTag(item.status)}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <div className="flex justify-end w-full mt-2">
        <Pagination
          current={current}
          pageSize={pageSize}
          total={data.length}
          onChange={setCurrent}
          showSizeChanger={false}
          className="!w-auto"
        />
      </div>
    </Spin>
  );
};

export default RequestTable;
